import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Clock, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import { supabase } from '../../lib/supabase';

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase?.from('blog_posts')?.select('id, title, slug, excerpt, cover_image_url, cover_image_alt, category, tags, read_time, published_at')?.eq('status', 'published')?.order('published_at', { ascending: false });
        if (fetchError) throw fetchError;
        setPosts(data || []);
      } catch (err) {
        setError(err?.message || 'Failed to load posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white font-body">
      <Helmet>
        <title>Angle! Blog – Photo Tips, Dating Advice & AI Insights</title>
        <meta name="description" content="Explore the Angle! Blog for expert photo tips, dating profile advice, and AI-powered insights to help you put your best face forward." />
        <link rel="canonical" href="https://angle.dating/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://angle.dating/blog" />
        <meta property="og:title" content="Angle! Blog – Photo Tips, Dating Advice & AI Insights" />
        <meta property="og:description" content="Explore the Angle! Blog for expert photo tips, dating profile advice, and AI-powered insights to help you put your best face forward." />
        <meta property="og:image" content="https://angle.dating/assets/images/3-top-images-1772836145108.png" />
        <meta property="og:site_name" content="Angle!" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Angle! Blog – Photo Tips, Dating Advice & AI Insights" />
        <meta name="twitter:description" content="Explore the Angle! Blog for expert photo tips, dating profile advice, and AI-powered insights." />
        <meta name="twitter:image" content="https://angle.dating/assets/images/3-top-images-1772836145108.png" />
      </Helmet>
      <Header
        user={null}
        onNavigate={() => {}}
        onPurchaseCredits={() => {}}
        onLogout={() => {}}
      />
      {/* Hero Banner */}
      <div className="relative w-full overflow-hidden" style={{ paddingTop: '64px' }}>
        <div className="relative h-52 md:h-64 bg-gradient-to-br from-gray-900 via-violet-950 to-gray-900 flex items-end">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/40 to-cyan-900/20" />
          <div className="absolute top-6 right-1/4 w-48 h-48 bg-violet-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-64 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 pb-8 w-full">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                <Sparkles size={14} className="text-white" />
              </div>
              <span className="text-white font-bold text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>Angle!</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>
              The Angle Blog
            </h1>
            <p className="text-violet-200/80 text-sm">
              Photo tips, dating advice, and AI insights to help you put your best face forward.
            </p>
          </div>
        </div>
      </div>
      {/* Blog Grid */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-violet-500" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center gap-2 py-20 text-red-500 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        ) : posts?.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">
            No posts published yet. Check back soon!
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {posts?.map((post) => (
              <motion.article
                key={post?.id}
                variants={cardVariants}
                className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <Link
                  to={`/blog/${post?.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden aspect-[16/10] bg-gray-100"
                >
                  {post?.cover_image_url ? (
                    <img
                      src={post?.cover_image_url}
                      alt={post?.cover_image_alt || post?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-violet-100 to-cyan-50 flex items-center justify-center">
                      <Sparkles size={32} className="text-violet-300" />
                    </div>
                  )}
                </Link>

                <div className="flex flex-col flex-1 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
                      {post?.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock size={11} />
                      {post?.read_time}
                    </span>
                  </div>

                  <Link
                    to={`/blog/${post?.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mb-2"
                  >
                    <h2 className="text-base font-bold text-gray-900 leading-snug group-hover:text-violet-700 transition-colors line-clamp-3">
                      {post?.title}
                    </h2>
                  </Link>

                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1 mb-4">
                    {post?.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                    <span className="text-xs text-gray-400">{formatDate(post?.published_at)}</span>
                    <Link
                      to={`/blog/${post?.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-800 transition-colors"
                    >
                      Read more <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}
      </main>
      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-14 px-6 mt-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                  <Sparkles size={15} className="text-white" />
                </div>
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>Angle!</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">AI-Powered Photo Rankings</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Product</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/purchase-credits" className="hover:text-gray-200 transition-colors">Pricing</Link></li>
                <li><Link to="/frequently-asked-questions" className="hover:text-gray-200 transition-colors">FAQ</Link></li>
                <li><Link to="/blog" className="hover:text-gray-200 transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Company</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-gray-200 transition-colors">Privacy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-gray-200 transition-colors">Terms</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Account</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><Link to="/auth" className="hover:text-gray-200 transition-colors">Sign In</Link></li>
                <li><Link to="/auth" className="hover:text-gray-200 transition-colors">Sign Up Free</Link></li>
                <li><Link to="/purchase-credits" className="hover:text-gray-200 transition-colors">Buy Credits</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">© {new Date()?.getFullYear()} Angle! All rights reserved.</p>
            <p className="text-xs text-gray-600">Made with ✨ AI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
